import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useFetch } from '../../../../components/component-items/helpers'
import { formatDateTime } from '../schedules/schedule-utils'

type ScheduleSummary = {
  is_active: boolean
  next_occurrence_local: string | null
  last_launch_status: string | null
  last_downstream_run: { status?: string } | null
  warehouse_timezone: string
}
type WaveOperationsSummaryProps = {
  profileResponse: any
  profilesLoaded: boolean
  scheduleResponse: any
  schedulesLoaded: boolean
  onOpenCoverage: () => void
}

const WaveOperationsOverview = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #dfe7f3;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(34, 46, 84, 0.06);
  overflow: hidden;

  .overview-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid #dfe7f3;
    background: linear-gradient(180deg, #fbfcff 0%, #f6f8fc 100%);

    h3 {
      margin: 0;
      color: #293241;
      font-size: 1.05rem;
      font-weight: 800;
    }

    p {
      margin: 0.25rem 0 0;
      color: #687386;
      font-size: 0.86rem;
    }
  }

  .overview-sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
    padding: 0.8rem;
  }

  .overview-section {
    min-width: 0;
  }

  .section-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
  }

  .overview-card {
    min-height: 88px;
    padding: 0.8rem 0.85rem;
    border: 1px solid #edf2f8;
    border-radius: 7px;
    background: #ffffff;
    &.clickable {
      cursor: pointer;
    }
  }

  .overview-card.attention .overview-value {
    color: #b45309;
  }

  .overview-card.warning {
    background: #fff9e6;
    border-color: #f5dfab;
  }

  .overview-label {
    display: block;
    color: #687386;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .overview-value {
    display: block;
    margin-top: 0.22rem;
    color: #293241;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.25;
  }

  .overview-note {
    display: block;
    margin-top: 0.2rem;
    color: #687386;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.35;
  }

  @media (max-width: 1100px) {
    .overview-sections {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 720px) {
    .overview-sections {
      padding: 0.7rem;
    }

    .section-grid {
      grid-template-columns: 1fr;
    }
  }
`

const normalizeScheduleSummary = (item: any): ScheduleSummary => ({
  is_active: Boolean(item.is_active),
  next_occurrence_local: item.next_occurrence_local || null,
  last_launch_status: item.last_launch_status || null,
  last_downstream_run: item.last_downstream_run || null,
  warehouse_timezone: item.warehouse_timezone || 'America/Los_Angeles',
})

const overviewValue = (loaded: boolean, value: number | string | null) => (loaded ? value : '-')

const WaveSummaryCard = ({
  label,
  value,
  note,
  className = '',
  onClick,
}: {
  label: string
  value: number | string | null
  note?: string
  className?: string
  onClick?: () => void
}) => (
  <div
    className={`overview-card ${className}`}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (event) => (event.key === 'Enter' || event.key === ' ') && onClick() : undefined}
  >
    <span className="overview-label">{label}</span>
    <span className="overview-value">{value}</span>
    {note ? <span className="overview-note">{note}</span> : null}
  </div>
)

export const WaveOperationsSummary = ({
  profileResponse,
  profilesLoaded,
  scheduleResponse,
  schedulesLoaded,
  onOpenCoverage,
}: WaveOperationsSummaryProps) => {
  const { response: coverageResponse, loaded: coverageLoaded }: any = useFetch(
    '/api/inventory/wave-profile/coverage/',
    {}
  )

  const profiles = useMemo(() => {
    if (Array.isArray(profileResponse)) return profileResponse
    if (Array.isArray(profileResponse?.results)) return profileResponse.results
    if (Array.isArray(profileResponse?.data)) return profileResponse.data
    if (Array.isArray(profileResponse?.profiles)) return profileResponse.profiles
    if (Array.isArray(profileResponse?.wave_profiles)) return profileResponse.wave_profiles
    return []
  }, [profileResponse])
  const schedules = useMemo<ScheduleSummary[]>(() => {
    const rawSchedules = Array.isArray(scheduleResponse)
      ? scheduleResponse
      : Array.isArray(scheduleResponse?.results)
        ? scheduleResponse.results
        : []
    return rawSchedules.map(normalizeScheduleSummary)
  }, [scheduleResponse])

  const profileMetrics = useMemo(() => {
    const directives = profiles.reduce(
      (total: number, profile: any) => total + (profile.wave_directives?.length || 0),
      0
    )
    return { directives }
  }, [profiles])

  const scheduleMetrics = useMemo(() => {
    const active = schedules.filter(({ is_active }) => is_active).length
    const nextSchedule = schedules
      .filter(({ is_active, next_occurrence_local }) => is_active && next_occurrence_local)
      .sort((left, right) => String(left.next_occurrence_local).localeCompare(String(right.next_occurrence_local)))[0]
    return { active, nextSchedule }
  }, [schedules])

  const schedulesReady = schedulesLoaded
  const profilesReady = profilesLoaded || Boolean(profileResponse)
  const ordersNotCovered = coverageLoaded ? (coverageResponse?.uncovered_orders ?? 0) : null
  const timezone =
    scheduleMetrics.nextSchedule?.warehouse_timezone || schedules[0]?.warehouse_timezone || 'America/Los_Angeles'
  const nextLocalRun = scheduleMetrics.nextSchedule
    ? formatDateTime(scheduleMetrics.nextSchedule.next_occurrence_local, true, timezone)
    : 'No upcoming run'
  const activeScheduleValue = overviewValue(schedulesReady, scheduleMetrics.active)

  return (
    <WaveOperationsOverview>
      <div className="overview-header">
        <h3>Wave Operations Summary</h3>
        <p>Automation health, profile readiness, and order coverage at a glance.</p>
      </div>
      <div className="overview-sections">
        <section className="overview-section">
          <div className="section-grid">
            <WaveSummaryCard label="Next Local Run" value={nextLocalRun} note={timezone} />
            <WaveSummaryCard label="Active Schedules" value={activeScheduleValue} />
          </div>
        </section>
        <section className="overview-section">
          <div className="section-grid">
            <WaveSummaryCard
              label="Wave Profiles"
              value={overviewValue(profilesReady, `${profiles.length} profiles`)}
              note={profilesReady ? `${profileMetrics.directives} directives` : undefined}
            />
            <WaveSummaryCard
              label="Orders Not Covered"
              value={overviewValue(coverageLoaded, ordersNotCovered)}
              className={`${ordersNotCovered && ordersNotCovered > 0 ? 'warning' : ''}${ordersNotCovered ? ' clickable' : ''}`}
              onClick={ordersNotCovered ? onOpenCoverage : undefined}
            />
          </div>
        </section>
      </div>
    </WaveOperationsOverview>
  )
}
